import { type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { Label } from "~/components/ui/label";
import { cn } from "~/lib/utils";

/**
 * Shared surface recipe for choices that change a form or matching selection.
 * Selection is always expressed by the teal boundary and control, never by the
 * darker cream surface on its own.
 */
const selectableOptionVariants = cva(
  "group/selectable-option flex min-h-14 items-center gap-3 rounded-lg border bg-background px-4 py-3 text-base font-medium leading-6 text-foreground transition-colors has-focus-visible:border-brand has-focus-visible:ring-2 has-focus-visible:ring-brand has-focus-visible:ring-offset-2 has-focus-visible:ring-offset-background",
  {
    variants: {
      selected: {
        false: "cursor-pointer border-input",
        true: "cursor-pointer border-brand bg-secondary ring-1 ring-brand",
      },
      disabled: {
        false: "",
        true: "cursor-not-allowed border-border bg-background text-muted-foreground",
      },
    },
    compoundVariants: [
      {
        disabled: false,
        selected: false,
        className: "hover:border-brand hover:bg-secondary",
      },
      {
        disabled: true,
        selected: true,
        className: "ring-0",
      },
    ],
    defaultVariants: {
      selected: false,
      disabled: false,
    },
  },
);

export type SelectableOptionProps = Omit<
  React.ComponentPropsWithoutRef<typeof Label>,
  "children"
> &
  VariantProps<typeof selectableOptionVariants> & {
    children: ReactNode;
    control: ReactNode;
    controlPosition?: "start" | "end";
  };

function SelectableOption({
  children,
  className,
  control,
  controlPosition = "start",
  disabled = false,
  selected = false,
  ...props
}: SelectableOptionProps) {
  return (
    <Label
      aria-disabled={disabled || undefined}
      data-selected={selected ? "true" : "false"}
      data-slot="selectable-option"
      className={cn(
        selectableOptionVariants({ selected, disabled }),
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "flex shrink-0 items-center",
          controlPosition === "end" && "order-last",
        )}
      >
        {control}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
    </Label>
  );
}

export { SelectableOption, selectableOptionVariants };
